/**
 * Metronome Engine V6.0 (Smart Metronome — Ultimate)
 * High-precision scheduler using Web Audio API
 *
 * FEATURES:
 * - Per-beat subdivision selection (each beat can have a different division)
 * - 8 subdivision types: 1(quarter), 2(8th), 3(triplet), 4(16th), 5(quintuplet),
 *   6(sextuplet), 7(septuplet), 8(32nd)
 * - Swing amount (50-75%) — replaces binary jazz mode
 * - Count-in (1-bar click before playing)
 * - Practice mode (play X bars / mute X bars)
 * - Accent strength control
 * - Tempo marking labels (Largo, Andante, Allegro, etc.)
 * - Screen flash on downbeat
 * - iOS AudioContext unlock
 * - Clave patterns, accent system, speed trainer, practice timer
 * - POLYRHYTHMS (X against Y)
 * - PRESETS & SETLISTS
 * - AUDIO EXPORT (WAV)
 * - BOUNCING BALL VISUAL
 */

class MetronomeEngine {
    constructor() {
        // AudioContext is created lazily on first user gesture (autoplay policy).
        this.ctx = null;
        this.masterGain = null;
        this.schedulerInterval = null;

        // State
        this.isPlaying = false;
        this.bpm = 120;
        this.nextNoteTime = 0.0;
        this.currentQuarterNote = 0;
        this.beatCount = 0;
        this.barCount = 0;

        // Settings
        this.lookahead = 25.0;
        this.scheduleAheadTime = 0.1;
        this.beatsPerBar = 4;

        // ===== PER-BEAT SUBDIVISIONS =====
        // Each element = number of subdivisions for that beat (1-8)
        // 1=quarter, 2=8th, 3=triplet, 4=16th, 5=quintuplet, 6=sextuplet, 7=septuplet, 8=32nd
        this.beatSubdivisions = [1, 1, 1, 1]; // Default: all quarter notes

        // Global subdivision volumes (used by mixer for fine control)
        this.volumes = {
            quarter: 1.0,
            eighth: 0.0,
            sixteenth: 0.0,
            triplet: 0.0,
            quintuplet: 0.0,
            sextuplet: 0.0,
            septuplet: 0.0,
            thirtySecond: 0.0
        };

        // Whether to use per-beat mode vs global mixer mode
        this.usePerBeatMode = true;

        // Subdivision metadata for display — warm palette only (no blues/purples)
        this.subdivisionInfo = {
            1: { name: '♩', label: 'Quarter', color: '#f4efe9' },
            2: { name: '2', label: '8ths', color: '#ff7a45' },
            3: { name: '3', label: 'Triplets', color: '#ffb36b' },
            4: { name: '4', label: '16ths', color: '#ffd4a8' },
            5: { name: '5', label: 'Quintuplets', color: '#f4efe9' },
            6: { name: '6', label: 'Sextuplets', color: '#ff7a45' },
            7: { name: '7', label: 'Septuplets', color: '#ffb36b' },
            8: { name: '8', label: '32nds', color: '#ffd4a8' }
        };

        // Advanced Features
        this.humanizeFactor = 0;
        this.jazzMode = false;
        this.swingAmount = 50; // 50 = straight, 67 = triplet swing, range 50-75
        this.soundBank = 'digital';
        this.useHaptics = false;
        this.accentStrength = 1.4; // Multiplier for accented beats (1.0 - 2.0)
        this.screenFlash = false;
        this.bouncingBall = false; // Visual option

        // Polyrhythm Engine
        this.polyrhythm = {
            enabled: false,
            primary: 4,   // e.g. 4
            secondary: 3, // e.g. 3 (playing 3 over 4)
            vol: 0.6,
            currentStep: 0
        };

        // Presets
        this.presets = this.loadPresets();

        // Count-In
        this.countIn = {
            enabled: false,
            beatsRemaining: 0,
            isCountingIn: false
        };

        // Practice Mode (play X bars, mute X bars — builds internal clock)
        this.practiceMode = {
            enabled: false,
            playBars: 4,
            muteBars: 2,
            currentBar: 0,
            isMuted: false
        };

        // Clave System
        this.claveEnabled = false;
        this.clavePattern = 'son_32';
        this.claveVolume = 0.7;
        this.claveEighthCounter = 0;
        this.clavePatterns = {
            son_32: { hits: [0, 3, 6, 10, 12], cycleLen: 16, label: 'Son 3-2' },
            son_23: { hits: [0, 4, 6, 10, 12], cycleLen: 16, label: 'Son 2-3' },
            rumba_32: { hits: [0, 3, 7, 10, 12], cycleLen: 16, label: 'Rumba 3-2' },
            bossa: { hits: [0, 3, 6, 10, 13], cycleLen: 16, label: 'Bossa Nova' },
            aksak_78: { hits: [0, 2, 4, 5], cycleLen: 7, label: '7/8 Aksak (2+2+3)' },
            balkan_58: { hits: [0, 2, 3], cycleLen: 5, label: '5/8 Balkan (2+3)' },
            zeybek_98: { hits: [0, 2, 4, 5, 7], cycleLen: 9, label: '9/8 Zeybek (2+2+2+3)' },
            tresillo: { hits: [0, 3, 6], cycleLen: 8, label: 'Tresillo (3+3+2)' },
            cascara: { hits: [0, 1, 3, 4, 6], cycleLen: 8, label: 'Cascara' },
            shiko: { hits: [0, 4, 6, 10, 12], cycleLen: 16, label: 'Shiko (West Africa)' },
            gahu: { hits: [0, 3, 6, 8, 10], cycleLen: 16, label: 'Gahu (Ghana)' }
        };

        // Accent Pattern System
        this.accentMode = 'standard';
        this.customAccents = [];

        // Speed Trainer
        this.speedTrainer = {
            enabled: false,
            startBpm: 80,
            targetBpm: 160,
            stepBpm: 4,
            barsPerStep: 4,
            currentBarInStep: 0,
            isComplete: false
        };

        // Practice Timer
        this.practiceTimer = {
            mode: 'countup',
            duration: 300,
            elapsed: 0,
            interval: null,
            isRunning: false
        };

        // Stats (persisted)
        this.stats = this.loadStats();
        this.sessionLog = []; // Temporary log for export/history in dashboard
        this.sessionStartTime = null;
    }

    // ===== PRESETS MANAGER =====
    loadPresets() {
        try {
            const raw = window.flPreferenceStorage.getItem('sa-tool-metronome-presets');
            if (raw) return JSON.parse(raw);
        } catch (e) { }

        // Default Starter Presets
        return [
            {
                id: 'def_rock', name: 'Classic Rock (Standard)', bpm: 120, beatsPerBar: 4,
                beatSubdivisions: [1, 1, 1, 1], jazzMode: false, swingAmount: 50, soundBank: 'drum',
                claveEnabled: false, polyrhythm: { enabled: false, primary: 4, secondary: 3, vol: 0.6 }
            },
            {
                id: 'def_reggae', name: 'Reggae One-Drop', bpm: 140, beatsPerBar: 4,
                beatSubdivisions: [1, 1, 1, 1], jazzMode: false, swingAmount: 66, soundBank: 'wood',
                // One drop logic handled by custom accents mostly, but here's a start
                accentStrength: 1.5, claveEnabled: false, polyrhythm: { enabled: false, primary: 4, secondary: 3, vol: 0.6 }
            },
            {
                id: 'def_motown', name: 'Motown Shuffle', bpm: 110, beatsPerBar: 4,
                beatSubdivisions: [1, 1, 1, 1], jazzMode: false, swingAmount: 62, soundBank: 'analog',
                claveEnabled: false, polyrhythm: { enabled: false, primary: 4, secondary: 3, vol: 0.6 }
            },
            {
                id: 'def_jazz', name: 'Jazz Swing 4/4', bpm: 130, beatsPerBar: 4,
                beatSubdivisions: [1, 1, 1, 1], jazzMode: true, swingAmount: 68, soundBank: 'digital',
                claveEnabled: false, polyrhythm: { enabled: false, primary: 4, secondary: 3, vol: 0.6 }
            },
            {
                id: 'def_poly', name: '3 against 4 Polyrhythm', bpm: 100, beatsPerBar: 4,
                beatSubdivisions: [1, 1, 1, 1], jazzMode: false, swingAmount: 50, soundBank: 'wood',
                claveEnabled: false, polyrhythm: { enabled: true, primary: 4, secondary: 3, vol: 0.7 }
            }
        ];
    }

    savePresets() {
        try { window.flPreferenceStorage.setItem('sa-tool-metronome-presets', JSON.stringify(this.presets)); } catch (e) { }
    }

    addPreset(name) {
        const preset = {
            id: Date.now().toString(36),
            name,
            bpm: this.bpm,
            beatsPerBar: this.beatsPerBar,
            beatSubdivisions: [...this.beatSubdivisions],
            jazzMode: this.jazzMode,
            swingAmount: this.swingAmount,
            soundBank: this.soundBank,
            claveEnabled: this.claveEnabled,
            clavePattern: this.clavePattern,
            polyrhythm: { ...this.polyrhythm },
            created: Date.now()
        };
        this.presets.push(preset);
        this.savePresets();
        return preset;
    }

    loadPreset(id) {
        const p = this.presets.find(x => x.id === id);
        if (!p) return false;

        this.bpm = p.bpm;
        this.beatsPerBar = p.beatsPerBar;
        this.beatSubdivisions = [...p.beatSubdivisions];
        this.jazzMode = p.jazzMode;
        this.swingAmount = p.swingAmount;
        this.soundBank = p.soundBank;

        // Advanced loading (safe fallbacks)
        if (p.claveEnabled !== undefined) this.claveEnabled = p.claveEnabled;
        if (p.clavePattern) this.clavePattern = p.clavePattern;
        if (p.polyrhythm) this.polyrhythm = { ...p.polyrhythm };

        // Trigger generic update event
        window.dispatchEvent(new CustomEvent('metronome:presetLoaded', { detail: p }));
        return true;
    }

    deletePreset(id) {
        this.presets = this.presets.filter(x => x.id !== id);
        this.savePresets();
        window.dispatchEvent(new CustomEvent('metronome:presetsUpdated'));
    }

    // ===== PER-BEAT SUBDIVISION API =====
    setSubdivisionForBeat(beatIndex, subdivCount) {
        if (beatIndex >= 0 && beatIndex < this.beatSubdivisions.length) {
            this.beatSubdivisions[beatIndex] = Math.max(1, Math.min(8, subdivCount));
            window.dispatchEvent(new CustomEvent('metronome:subdivChanged', {
                detail: { beat: beatIndex, subdiv: this.beatSubdivisions[beatIndex], all: [...this.beatSubdivisions] }
            }));
        }
    }

    cycleSubdivisionForBeat(beatIndex) {
        if (beatIndex >= 0 && beatIndex < this.beatSubdivisions.length) {
            const current = this.beatSubdivisions[beatIndex];
            const next = current >= 8 ? 1 : current + 1;
            this.setSubdivisionForBeat(beatIndex, next);
            return next;
        }
        return 1;
    }

    setAllSubdivisions(subdivCount) {
        for (let i = 0; i < this.beatSubdivisions.length; i++) {
            this.beatSubdivisions[i] = Math.max(1, Math.min(8, subdivCount));
        }
        window.dispatchEvent(new CustomEvent('metronome:subdivChanged', {
            detail: { beat: -1, subdiv: subdivCount, all: [...this.beatSubdivisions] }
        }));
    }

    syncBeatSubdivisions() {
        // Ensure beatSubdivisions array matches current beatsPerBar
        while (this.beatSubdivisions.length < this.beatsPerBar) {
            this.beatSubdivisions.push(1);
        }
        this.beatSubdivisions.length = this.beatsPerBar;
    }

    // ===== STATS PERSISTENCE =====
    loadStats() {
        try {
            const raw = window.flPreferenceStorage.getItem('sa-tool-metronome-stats');
            if (raw) return JSON.parse(raw);
        } catch (e) { }
        return { totalBeats: 0, totalSeconds: 0, sessions: 0, lastSession: null };
    }

    saveStats() {
        try { window.flPreferenceStorage.setItem('sa-tool-metronome-stats', JSON.stringify(this.stats)); } catch (e) { }
    }

    // ===== AUDIO CONTEXT =====
    _initAudio() {
        if (this.ctx) return;
        this.ctx = new (window.AudioContext || window.webkitAudioContext)();
        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.value = 0.8;
        this.masterGain.connect(this.ctx.destination);
    }

    resumeContext() {
        this._initAudio();
        if (this.ctx.state === 'suspended') this.ctx.resume();
    }

    // ===== TEMPO MARKING =====
    static getTempoMarking(bpm) {
        if (bpm <= 24) return 'Larghissimo';
        if (bpm <= 40) return 'Grave';
        if (bpm <= 55) return 'Largo';
        if (bpm <= 65) return 'Larghetto';
        if (bpm <= 72) return 'Adagio';
        if (bpm <= 80) return 'Andante';
        if (bpm <= 95) return 'Moderato';
        if (bpm <= 109) return 'Allegretto';
        if (bpm <= 132) return 'Allegro';
        if (bpm <= 140) return 'Vivace';
        if (bpm <= 167) return 'Presto';
        return 'Prestissimo';
    }

    // ===== PLAYBACK =====
    start() {
        this._initAudio();
        this.resumeContext();
        if (this.isPlaying) return;

        // Count-in
        if (this.countIn.enabled) {
            this.countIn.isCountingIn = true;
            this.countIn.beatsRemaining = this.beatsPerBar;
        } else {
            this.countIn.isCountingIn = false;
        }

        this.isPlaying = true;
        this.currentQuarterNote = 0;
        this.beatCount = 0;
        this.barCount = 0;
        this.claveEighthCounter = 0;
        this.nextNoteTime = this.ctx.currentTime + 0.05;

        // Practice mode reset
        this.practiceMode.currentBar = 0;
        this.practiceMode.isMuted = false;

        // Speed trainer reset
        if (this.speedTrainer.enabled) {
            this.bpm = this.speedTrainer.startBpm;
            this.speedTrainer.currentBarInStep = 0;
            this.speedTrainer.isComplete = false;
            window.dispatchEvent(new CustomEvent('metronome:trainerUpdate', {
                detail: this.getTrainerProgress()
            }));
        }

        // Practice timer
        this.startPracticeTimer();

        // Stats
        this.stats.sessions++;
        this.sessionStartTime = Date.now();

        this.schedulerInterval = setInterval(() => this.scheduler(), this.lookahead);
        window.dispatchEvent(new CustomEvent('metronome:start'));
    }

    stop() {
        this.isPlaying = false;
        clearInterval(this.schedulerInterval);
        this.stopPracticeTimer();

        // Log session duration
        if (this.sessionStartTime) {
            const durationSec = (Date.now() - this.sessionStartTime) / 1000;
            if (durationSec > 10) { // Only log meaningful sessions
                this.sessionLog.push({
                    date: new Date().toISOString(),
                    duration: durationSec,
                    bpmStart: this.bpm, // Simple logging for now
                    beats: this.stats.totalBeats
                });
                // In a real app, we'd persist this history or sync it
            }
        }

        this.saveStats();
        window.dispatchEvent(new CustomEvent('metronome:stop'));
    }

    toggle() {
        this.isPlaying ? this.stop() : this.start();
    }

    scheduler() {
        while (this.nextNoteTime < this.ctx.currentTime + this.scheduleAheadTime) {
            this.scheduleNote(this.currentQuarterNote, this.nextNoteTime);
            this.nextNote();
        }
    }

    nextNote() {
        const secondsPerBeat = 60.0 / this.bpm;
        this.nextNoteTime += secondsPerBeat;
        this.currentQuarterNote++;
        this.beatCount++;
        this.stats.totalBeats++;
        this.claveEighthCounter += 2;

        if (this.currentQuarterNote === this.beatsPerBar) {
            this.currentQuarterNote = 0;

            // Polyrhythm Reset (sync every bar)
            this.polyrhythm.currentStep = 0;

            // Count-in completion
            if (this.countIn.isCountingIn) {
                this.countIn.isCountingIn = false;
                this.beatCount = 0;
                this.barCount = 0;
                window.dispatchEvent(new CustomEvent('metronome:countInDone'));
            } else {
                this.barCount++;
            }

            // Practice mode — toggle mute after X bars
            if (this.practiceMode.enabled && !this.countIn.isCountingIn) {
                this.practiceMode.currentBar++;
                const totalCycle = this.practiceMode.playBars + this.practiceMode.muteBars;
                const posInCycle = this.practiceMode.currentBar % totalCycle;
                const wasMuted = this.practiceMode.isMuted;
                this.practiceMode.isMuted = posInCycle >= this.practiceMode.playBars;
                if (wasMuted !== this.practiceMode.isMuted) {
                    window.dispatchEvent(new CustomEvent('metronome:practiceToggle', {
                        detail: { isMuted: this.practiceMode.isMuted }
                    }));
                }
            }

            // Speed Trainer logic
            if (this.speedTrainer.enabled && !this.speedTrainer.isComplete) {
                this.speedTrainer.currentBarInStep++;
                if (this.speedTrainer.currentBarInStep >= this.speedTrainer.barsPerStep) {
                    this.speedTrainer.currentBarInStep = 0;
                    this.bpm += this.speedTrainer.stepBpm;

                    if (this.bpm >= this.speedTrainer.targetBpm) {
                        this.bpm = this.speedTrainer.targetBpm;
                        this.speedTrainer.isComplete = true;
                    }

                    window.dispatchEvent(new CustomEvent('metronome:trainerUpdate', {
                        detail: this.getTrainerProgress()
                    }));
                    window.dispatchEvent(new CustomEvent('metronome:bpmChange', { detail: { bpm: this.bpm } }));
                }
            }
        }
    }

    getTrainerProgress() {
        const st = this.speedTrainer;
        const range = st.targetBpm - st.startBpm;
        const progress = range > 0 ? Math.min(1, (this.bpm - st.startBpm) / range) : 1;
        return {
            currentBpm: this.bpm,
            startBpm: st.startBpm,
            targetBpm: st.targetBpm,
            progress,
            isComplete: st.isComplete,
            barsRemaining: st.barsPerStep - st.currentBarInStep
        };
    }

    // ===== NOTE SCHEDULING =====
    scheduleNote(beatNumber, time) {
        let microTiming = 0;
        if (this.humanizeFactor > 0) {
            microTiming = (Math.random() - 0.5) * 0.03 * this.humanizeFactor;
        }

        const beatTime = time + microTiming;
        const qLen = 60.0 / this.bpm;

        // Determine if this beat is accented
        const isAccented = this.isAccentedBeat(beatNumber);

        // Visual Trigger
        this.scheduleVisual(beatNumber, beatTime, isAccented);

        // ---- PRACTICE MODE MUTING ----
        const isMutedBeat = this.practiceMode.enabled && this.practiceMode.isMuted && !this.countIn.isCountingIn;

        // ---- POLYRHYTHM LAYER (Secondary) ----
        if (this.polyrhythm.enabled && !isMutedBeat && !this.countIn.isCountingIn && this.polyrhythm.vol > 0) {
            const polyRatio = this.polyrhythm.secondary / this.polyrhythm.primary; // e.g. 3/4 = 0.75
            // Determine playing speed relative to bar
            // Primary = beatsPerBar. Secondary = polysteps per bar.
            // Bar duration = beatsPerBar * qLen

            // Re-calc based on bar duration to be safe
            const barDuration = qLen * this.beatsPerBar;
            const stepDuration = barDuration / this.polyrhythm.secondary;

            // We are at beatNumber (0-indexed). 
            // This is ONLY called at quarter note intervals. 
            // We need a separate scheduler for polyrhythms ideally, OR schedule the poly steps that fall within this beat.

            // To be precise: schedule ALL poly steps for the next beat duration.
            const beatStart = beatNumber * qLen; // relative to bar start
            const beatEnd = (beatNumber + 1) * qLen;

            for (let i = 0; i < this.polyrhythm.secondary; i++) {
                const polyStart = i * stepDuration;
                // If this poly step falls within current beat window
                if (polyStart >= beatStart - 0.001 && polyStart < beatEnd - 0.001) {
                    // Calculate offset from current beat time
                    const offset = polyStart - beatStart;
                    // Avoid double-playing the downbeat if it's perfectly aligned (beat 0) 
                    // unless we want distinct poly sound overlay
                    const isDownbeat = (Math.abs(polyStart) < 0.001);

                    if (!isDownbeat || this.soundBank === 'digital') {
                        this.playSound('poly', beatTime + offset, this.polyrhythm.vol);

                        // Visual event for polyrhythm
                        const vDelay = Math.max(0, (beatTime + offset - this.ctx.currentTime) * 1000);
                        setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('metronome:polyBeat', { detail: { step: i, total: this.polyrhythm.secondary } }));
                        }, vDelay);
                    }
                }
            }
        }

        // ---- MAIN CLICK (quarter note) ----
        if (!isMutedBeat && (this.volumes.quarter > 0 || (this.jazzMode && this.beatsPerBar === 4))) {
            let vol = this.volumes.quarter;
            let type = 'quarter';

            if (this.jazzMode && this.beatsPerBar === 4) {
                if (beatNumber === 0 || beatNumber === 2) {
                    vol *= 0.1;
                } else {
                    vol = Math.max(0.8, vol * 1.2);
                    type = 'jazz_accent';
                }
            } else if (isAccented) {
                vol *= this.accentStrength;
                type = 'accent';
            } else if (beatNumber === 0) {
                vol *= (1 + (this.accentStrength - 1) * 0.5);
                type = 'accent';
            }

            if (vol > 0.01) this.playSound(type, beatTime, vol);
        }

        // ---- PER-BEAT SUBDIVISIONS ----
        if (!isMutedBeat && this.usePerBeatMode) {
            const subdivCount = this.beatSubdivisions[beatNumber] || 1;
            if (subdivCount > 1) {
                this.schedulePerBeatSubdivisions(beatNumber, beatTime, qLen, subdivCount);
            }
        }

        // ---- GLOBAL MIXER SUBDIVISIONS (legacy/fine-tune mode) ----
        if (!isMutedBeat && !this.usePerBeatMode) {
            // Apply swing to 8th note subdivisions
            const swingRatio = this.swingAmount / 100; // 0.5 = straight, 0.67 = triplet swing
            const swingOffset = qLen * swingRatio;

            if (this.volumes.eighth > 0) {
                this.playSound('eighth', beatTime + swingOffset, this.volumes.eighth);
            }
            if (this.volumes.triplet > 0) {
                this.playSound('triplet', beatTime + (qLen * 1 / 3), this.volumes.triplet);
                this.playSound('triplet', beatTime + (qLen * 2 / 3), this.volumes.triplet);
            }
            if (this.volumes.sixteenth > 0) {
                this.playSound('sixteenth', beatTime + (qLen * 0.25), this.volumes.sixteenth);
                this.playSound('sixteenth', beatTime + swingOffset, this.volumes.sixteenth);
                this.playSound('sixteenth', beatTime + (qLen * 0.75), this.volumes.sixteenth);
            }
            if (this.volumes.quintuplet > 0) {
                for (let i = 1; i < 5; i++) {
                    this.playSound('quintuplet', beatTime + (qLen * i / 5), this.volumes.quintuplet);
                }
            }
            if (this.volumes.sextuplet > 0) {
                for (let i = 1; i < 6; i++) {
                    this.playSound('sextuplet', beatTime + (qLen * i / 6), this.volumes.sextuplet);
                }
            }
            if (this.volumes.septuplet > 0) {
                for (let i = 1; i < 7; i++) {
                    this.playSound('septuplet', beatTime + (qLen * i / 7), this.volumes.septuplet);
                }
            }
            if (this.volumes.thirtySecond > 0) {
                for (let i = 1; i < 8; i++) {
                    this.playSound('thirtySecond', beatTime + (qLen * i / 8), this.volumes.thirtySecond);
                }
            }
        }

        // ---- CLAVE OVERLAY ----
        if (this.claveEnabled) {
            this.scheduleClave(beatNumber, beatTime, qLen);
        }

        // ---- SUBDIVISION GRID EVENTS ----
        this.scheduleGridEvents(beatNumber, beatTime, qLen);
    }

    // ===== PER-BEAT SUBDIVISION SCHEDULING =====
    schedulePerBeatSubdivisions(beatNumber, beatTime, qLen, subdivCount) {
        // subdivCount: 2=8ths, 3=triplets, 4=16ths, 5=quintuplets, 6=sextuplets, 7=septuplets, 8=32nds
        const subdivTypeMap = {
            2: 'eighth',
            3: 'triplet',
            4: 'sixteenth',
            5: 'quintuplet',
            6: 'sextuplet',
            7: 'septuplet',
            8: 'thirtySecond'
        };
        const type = subdivTypeMap[subdivCount] || 'eighth';
        const swingRatio = this.swingAmount / 100;

        // Schedule all subdivisions except the first (which is the quarter note itself)
        for (let i = 1; i < subdivCount; i++) {
            let offset = (qLen * i) / subdivCount;

            // Apply swing to the "and" of each beat (subdivision index 1 of 2-based divisions)
            if (subdivCount === 2 && i === 1) {
                offset = qLen * swingRatio;
            } else if (subdivCount === 4 && i === 2) {
                // Swing the 3rd 16th (the "and")
                offset = qLen * swingRatio;
            }

            let vol = 0.65; // Default subdivision volume

            // Humanize volume slightly
            if (this.humanizeFactor > 0) {
                const drift = (Math.random() - 0.5) * 0.15 * this.humanizeFactor;
                vol = Math.max(0.1, Math.min(1, vol + drift));
            }

            this.playSound(type, beatTime + offset, vol);

            // Fire visual event for each subdivision
            const delay = Math.max(0, (beatTime + offset - this.ctx.currentTime) * 1000);
            const beatIdx = beatNumber;
            const subIdx = i;
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('metronome:subdivPulse', {
                    detail: { beat: beatIdx, subIndex: subIdx, subdivCount, type }
                }));
            }, delay);
        }
    }

    // ===== ACCENT SYSTEM =====
    isAccentedBeat(beatNumber) {
        switch (this.accentMode) {
            case 'standard':
                return beatNumber === 0;
            case 'backbeat':
                return (this.beatsPerBar === 4) ? (beatNumber === 1 || beatNumber === 3) : (beatNumber === 0);
            case 'one_drop':
                return beatNumber === 0;
            case 'every_other':
                return beatNumber % 2 === 0;
            case 'progressive':
                return beatNumber === (this.barCount % this.beatsPerBar);
            case 'custom':
                return this.customAccents[beatNumber] || false;
            default:
                return beatNumber === 0;
        }
    }

    setCustomAccents(accents) {
        this.customAccents = accents;
    }

    // ===== CLAVE SYSTEM (POLYMETRIC) =====
    scheduleClave(beatNumber, beatTime, qLen) {
        const patDef = this.clavePatterns[this.clavePattern];
        if (!patDef) return;

        const { hits, cycleLen } = patDef;

        for (let sub = 0; sub < 2; sub++) {
            const globalEighth = this.claveEighthCounter + sub;
            const posInCycle = globalEighth % cycleLen;

            if (hits.includes(posInCycle)) {
                const offset = sub * (qLen * 0.5);
                this.playSound('clave', beatTime + offset, this.claveVolume);

                const delay = Math.max(0, (beatTime + offset - this.ctx.currentTime) * 1000);
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('metronome:claveHit', {
                        detail: { posInCycle, cycleLen }
                    }));
                }, delay);
            }
        }
    }

    // ===== SUBDIVISION GRID EVENTS =====
    scheduleGridEvents(beatNumber, beatTime, qLen) {
        const cells = [];

        if (this.volumes.quarter > 0.01) cells.push({ pos: 0, type: 'quarter' });

        if (this.usePerBeatMode) {
            const subdivCount = this.beatSubdivisions[beatNumber] || 1;
            for (let i = 1; i < subdivCount; i++) {
                cells.push({ pos: i / subdivCount, type: 'subdiv' });
            }
        } else {
            if (this.volumes.eighth > 0) cells.push({ pos: 0.5, type: 'eighth' });
            if (this.volumes.sixteenth > 0) {
                cells.push({ pos: 0.25, type: 'sixteenth' });
                cells.push({ pos: 0.5, type: 'sixteenth' });
                cells.push({ pos: 0.75, type: 'sixteenth' });
            }
            if (this.volumes.triplet > 0) {
                cells.push({ pos: 1 / 3, type: 'triplet' });
                cells.push({ pos: 2 / 3, type: 'triplet' });
            }
        }

        cells.forEach(cell => {
            const delay = Math.max(0, (beatTime + cell.pos * qLen - this.ctx.currentTime) * 1000);
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('metronome:gridPulse', {
                    detail: { beat: beatNumber, position: cell.pos, type: cell.type }
                }));
            }, delay);
        });
    }

    // ===== VISUAL SCHEDULING =====
    scheduleVisual(beatNumber, time, isAccented) {
        const delay = Math.max(0, (time - this.ctx.currentTime) * 1000);
        const isCountingIn = this.countIn.isCountingIn;
        const isMuted = this.practiceMode.enabled && this.practiceMode.isMuted && !isCountingIn;

        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('metronome:beat', {
                detail: {
                    beat: beatNumber,
                    isDownbeat: beatNumber === 0,
                    isAccented,
                    isJazzAccent: (this.jazzMode && (beatNumber === 1 || beatNumber === 3)),
                    totalBeats: this.beatCount,
                    bar: this.barCount,
                    isCountingIn,
                    isMuted
                }
            }));

            // Bouncing Ball Update
            if (this.bouncingBall && !isMuted) {
                window.dispatchEvent(new CustomEvent('metronome:ballUpdate', {
                    detail: { progress: (beatNumber / this.beatsPerBar) }
                }));
            }

            // Screen flash on downbeat
            if (this.screenFlash && beatNumber === 0 && !isMuted) {
                window.dispatchEvent(new CustomEvent('metronome:flash'));
            }

            if (this.useHaptics && navigator.vibrate) {
                if (isAccented || beatNumber === 0) navigator.vibrate(12);
                else navigator.vibrate(6);
            }
        }, delay);
    }

    // ===== SOUND SYNTHESIS =====
    playSound(type, time, volume, context, dest) {
        if (context === undefined) context = this.ctx;
        if (dest === undefined) dest = this.masterGain;
        if (!context) return;
        if (this.humanizeFactor > 0 && context === this.ctx) {
            const drift = (Math.random() - 0.5) * 0.2 * this.humanizeFactor;
            volume = Math.max(0, Math.min(1, volume + drift));
        }

        // Route clave to dedicated sound - special case: assumes main context for now
        if (type === 'clave' && context === this.ctx) {
            this.playClaveSound(time, volume);
            return;
        }

        const osc = context.createOscillator();
        const gain = context.createGain();
        osc.connect(gain);

        // If we are passed a destination (for offline rendering), use it
        // Otherwise connect to master
        if (dest) gain.connect(dest);
        else gain.connect(context.destination);

        let freq = 1000;
        let typeW = 'triangle';
        let decay = 0.05;

        // Sound bank routing
        switch (this.soundBank) {
            case 'analog':
                typeW = 'square';
                freq = type === 'accent' ? 1600 : type === 'quarter' ? 800 : type === 'poly' ? 1200 : type === 'jazz_accent' ? 1200 : 600;
                break;
            case 'wood':
                typeW = 'sine';
                decay = 0.03;
                freq = type === 'accent' ? 1200 : type === 'quarter' ? 900 : type === 'poly' ? 1050 : 700;
                break;
            case 'drum':
                typeW = 'sine';
                if (type === 'accent' || type === 'quarter') {
                    freq = 150; decay = 0.1;
                    if (context === this.ctx) this.createNoise(time, 0.01, volume * 0.5);
                } else if (type === 'jazz_accent') {
                    if (context === this.ctx) this.createNoise(time, 0.05, volume);
                    freq = 400;
                } else {
                    if (context === this.ctx) this.createNoise(time, 0.02, volume * 0.5);
                    freq = 800; volume *= 0.5;
                }
                break;
            case 'clave_bank':
                typeW = 'triangle';
                decay = 0.025;
                freq = type === 'accent' ? 2800 : type === 'quarter' ? 2200 : type === 'jazz_accent' ? 2500 : 1800;
                // Complex tone simulation not fully supported in offline export yet, fallback to simple tone
                if (context === this.ctx) this.playTone(freq * 1.5, time, 0.015, volume * 0.3, 'sine');
                break;
            case 'cowbell':
                typeW = 'square';
                decay = 0.06;
                freq = type === 'accent' ? 800 : type === 'quarter' ? 560 : type === 'jazz_accent' ? 680 : 420;
                if (context === this.ctx) this.playTone(freq * 1.504, time, decay, volume * 0.6, 'square');
                break;
            default: // digital
                typeW = 'triangle';
                freq = type === 'accent' ? 2200 : type === 'quarter' ? 1200 : type === 'poly' ? 1500 : type === 'jazz_accent' ? 1800 : 800;
                break;
        }

        // Subdivision pitch mapping — each type gets a distinct pitch character
        const pitchMap = {
            'eighth': 1.2,
            'sixteenth': 1.5,
            'triplet': 1.3,
            'quintuplet': 1.4,
            'sextuplet': 1.55,
            'septuplet': 1.6,
            'thirtySecond': 1.7,
            'poly': 1.25 // Polyrhythm pitch
        };
        if (pitchMap[type]) freq *= pitchMap[type];

        osc.type = typeW;
        osc.frequency.setValueAtTime(freq, time);

        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(volume, time + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.001, time + decay);

        osc.start(time);
        osc.stop(time + decay + 0.1);
    }

    playClaveSound(time, volume) {
        if (!this.ctx) return;
        const osc1 = this.ctx.createOscillator();
        const osc2 = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(this.masterGain);

        osc1.type = 'triangle';
        osc1.frequency.setValueAtTime(2500, time);
        osc1.frequency.exponentialRampToValueAtTime(1800, time + 0.02);

        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(3200, time);

        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(volume, time + 0.002);
        gain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);

        osc1.start(time);
        osc1.stop(time + 0.08);
        osc2.start(time);
        osc2.stop(time + 0.08);
    }

    playTone(freq, time, decay, volume, type) {
        if (!this.ctx) return;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.connect(gain);
        gain.connect(this.masterGain);
        osc.type = type || 'sine';
        osc.frequency.setValueAtTime(freq, time);
        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(volume, time + 0.003);
        gain.gain.exponentialRampToValueAtTime(0.001, time + decay);
        osc.start(time);
        osc.stop(time + decay + 0.1);
    }

    createNoise(time, duration, volume) {
        if (!this.ctx) return;
        const bufferSize = this.ctx.sampleRate * duration;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

        const noise = this.ctx.createBufferSource();
        noise.buffer = buffer;
        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(volume, time);
        gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

        noise.connect(gain);
        gain.connect(this.masterGain);
        noise.start(time);
    }

    // ===== PRACTICE TIMER =====
    startPracticeTimer() {
        this.practiceTimer.elapsed = 0;
        this.practiceTimer.isRunning = true;

        this.practiceTimer.interval = setInterval(() => {
            this.practiceTimer.elapsed++;
            this.stats.totalSeconds++;

            const evt = {
                elapsed: this.practiceTimer.elapsed,
                mode: this.practiceTimer.mode,
                remaining: this.practiceTimer.mode === 'countdown'
                    ? Math.max(0, this.practiceTimer.duration - this.practiceTimer.elapsed) : 0
            };

            window.dispatchEvent(new CustomEvent('metronome:timerTick', { detail: evt }));

            if (this.practiceTimer.mode === 'countdown' && this.practiceTimer.elapsed >= this.practiceTimer.duration) {
                this.stop();
                window.dispatchEvent(new CustomEvent('metronome:timerDone'));
            }
        }, 1000);
    }

    stopPracticeTimer() {
        this.practiceTimer.isRunning = false;
        clearInterval(this.practiceTimer.interval);
    }

    // ===== BPM HELPERS =====
    setBpm(val) {
        this.bpm = Math.max(30, Math.min(300, val));
        window.dispatchEvent(new CustomEvent('metronome:bpmChange', { detail: { bpm: this.bpm } }));
    }

    setMasterVolume(val) {
        if (!this.ctx || !this.masterGain) return;
        this.masterGain.gain.setTargetAtTime(Math.max(0, Math.min(1, val)), this.ctx.currentTime, 0.02);
    }

    // ===== AUDIO EXPORT =====
    async renderAudioExport(bars = 8) {
        // Needs OfflineAudioContext
        const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OfflineContext) return null;

        const totalBeats = bars * this.beatsPerBar;
        const qLen = 60.0 / this.bpm;
        const duration = totalBeats * qLen;
        // Add a little tail for reverb/decay
        const totalSamples = Math.ceil((duration + 2) * 44100);

        const offlineCtx = new OfflineContext(1, totalSamples, 44100);

        // Iterate through bars and beats to schedule
        // NOTE: We only schedule the basic click and polyrhythm for export
        // Complex features like speed trainer or practice mode muting are ignored for export (clean click track)

        for (let b = 0; b < bars; b++) {
            for (let beat = 0; beat < this.beatsPerBar; beat++) {
                const time = (b * this.beatsPerBar + beat) * qLen;

                // 1. Accents
                const isAccented = this.isAccentedBeat(beat);

                // 2. Main Click
                if (this.volumes.quarter > 0 || (this.jazzMode && this.beatsPerBar === 4)) {
                    let vol = this.volumes.quarter;
                    let type = 'quarter';

                    if (this.jazzMode && this.beatsPerBar === 4) {
                        if (beat === 0 || beat === 2) vol *= 0.1;
                        else { vol = Math.max(0.8, vol * 1.2); type = 'jazz_accent'; }
                    } else if (isAccented) {
                        vol *= this.accentStrength; type = 'accent';
                    } else if (beat === 0) {
                        vol *= (1 + (this.accentStrength - 1) * 0.5); type = 'accent';
                    }

                    if (vol > 0.01) this.playSound(type, time, vol, offlineCtx, null);
                }

                // 3. Subdivisions (Per-beat)
                if (this.usePerBeatMode) {
                    const subdivCount = this.beatSubdivisions[beat] || 1;
                    if (subdivCount > 1) {
                        const typeMap = { 2: 'eighth', 3: 'triplet', 4: 'sixteenth', 5: 'quintuplet', 6: 'sextuplet', 7: 'septuplet', 8: 'thirtySecond' };
                        const sType = typeMap[subdivCount] || 'eighth';
                        const swingRatio = this.swingAmount / 100;

                        for (let i = 1; i < subdivCount; i++) {
                            let offset = (qLen * i) / subdivCount;
                            if (subdivCount === 2 && i === 1) offset = qLen * swingRatio;
                            else if (subdivCount === 4 && i === 2) offset = qLen * swingRatio;

                            this.playSound(sType, time + offset, 0.65, offlineCtx, null);
                        }
                    }
                }

                // 4. Polyrhythm
                if (this.polyrhythm.enabled && this.polyrhythm.vol > 0) {
                    const barDuration = qLen * this.beatsPerBar;
                    const stepDuration = barDuration / this.polyrhythm.secondary;
                    const beatStart = beat * qLen; // relative to bar
                    const beatEnd = (beat + 1) * qLen;

                    // Iterate all possible poly steps in this bar
                    for (let i = 0; i < this.polyrhythm.secondary; i++) {
                        const polyStart = i * stepDuration;
                        // If falls in this beat
                        if (polyStart >= beatStart - 0.001 && polyStart < beatEnd - 0.001) {
                            const offset = polyStart - beatStart;
                            const isDownbeat = (Math.abs(polyStart) < 0.001);
                            if (!isDownbeat || this.soundBank === 'digital') {
                                this.playSound('poly', time + offset, this.polyrhythm.vol, offlineCtx, null);
                            }
                        }
                    }
                }
            }
        }

        // Render
        const renderedBuffer = await offlineCtx.startRendering();
        return renderedBuffer;
    }
}


// ========================================
// UI CONTROLLER V6 — Smart Metronome Ultimate
// ========================================
document.addEventListener('DOMContentLoaded', () => {
    window.flRunWhenPrivacyReady(() => {
    if (!document.querySelector('.metronome-app')) return;

    const engine = new MetronomeEngine();

    // ---- UI REFS ----
    const ui = {
        playBtn: document.getElementById('startStopBtn'),
        bpmDisplay: document.getElementById('bpmDisplay'),
        bpmSlider: document.getElementById('bpmSlider'),
        tapBtn: document.getElementById('tapTempoBtn'),
        bpmMinus: document.getElementById('bpmMinus'),
        bpmPlus: document.getElementById('bpmPlus'),

        // Mixer
        volQuarter: document.getElementById('volQuarter'),
        volEighth: document.getElementById('volEighth'),
        volSixteenth: document.getElementById('volSixteenth'),
        volTriplet: document.getElementById('volTriplet'),
        volQuintuplet: document.getElementById('volQuintuplet'),
        volSextuplet: document.getElementById('volSextuplet'),
        volSeptuplet: document.getElementById('volSeptuplet'),
        volThirtySecond: document.getElementById('volThirtySecond'),

        // Feel & Sound
        humanize: document.getElementById('humanizeSlider'),
        humanizeVal: document.getElementById('humanizeValue'),
        jazzMode: document.getElementById('jazzModeToggle'),
        soundBank: document.getElementById('soundSelect'),
        haptics: document.getElementById('hapticsToggle'),

        // Visuals
        ring: document.getElementById('visualizerRing'),
        jazzIcon: document.getElementById('jazzIcon'),

        // Time Sig
        timeSigPlus: document.getElementById('timeSigPlus'),
        timeSigMinus: document.getElementById('timeSigMinus'),
        beatsPerBar: document.getElementById('beatsPerBarDisp'),
        beatDots: document.getElementById('beatDots'),

        // Subdivision selector
        subdivSelector: document.getElementById('subdivSelector'),
        subdivLegend: document.getElementById('subdivLegend'),

        // Clave
        claveToggle: document.getElementById('claveToggle'),
        claveSelect: document.getElementById('claveSelect'),
        claveVolume: document.getElementById('claveVolume'),
        claveVisual: document.getElementById('claveVisual'),

        // Accent
        accentSelect: document.getElementById('accentSelect'),
        accentGrid: document.getElementById('accentGrid'),

        // Speed Trainer
        trainerToggle: document.getElementById('trainerToggle'),
        trainerStart: document.getElementById('trainerStart'),
        trainerTarget: document.getElementById('trainerTarget'),
        trainerStep: document.getElementById('trainerStep'),
        trainerBars: document.getElementById('trainerBars'),
        trainerProgress: document.getElementById('trainerProgress'),
        trainerBpmLabel: document.getElementById('trainerBpmLabel'),

        // Timer
        timerDisplay: document.getElementById('timerDisplay'),
        totalBeatsDisp: document.getElementById('totalBeatsDisplay'),

        // Stats
        statBeats: document.getElementById('statTotalBeats'),
        statTime: document.getElementById('statTotalTime'),
        statSessions: document.getElementById('statSessions'),

        // Grid
        subdivGrid: document.getElementById('subdivisionGrid'),

        // New V6 elements
        tempoMarking: document.getElementById('tempoMarking'),
        swingSlider: document.getElementById('swingSlider'),
        swingValue: document.getElementById('swingValue'),
        accentStrengthSlider: document.getElementById('accentStrengthSlider'),
        accentStrengthValue: document.getElementById('accentStrengthValue'),
        screenFlashToggle: document.getElementById('screenFlashToggle'),
        countInToggle: document.getElementById('countInToggle'),
        countInBadge: document.getElementById('countInBadge'),
        practiceModeToggle: document.getElementById('practiceModeToggle'),
        playBarsSlider: document.getElementById('playBarsSlider'),
        muteBarsSlider: document.getElementById('muteBarsSlider'),
        practiceStatus: document.getElementById('practiceStatus'),
        screenFlashOverlay: document.getElementById('screenFlash'),

        // New V7 elements
        polyToggle: document.getElementById('polyToggle'),
        polyControls: document.getElementById('polyControls'),
        polyPrimaryDisp: document.getElementById('polyPrimaryDisp'),
        polySecondaryDisp: document.getElementById('polySecondaryDisp'),
        polySecondarySlider: document.getElementById('polySecondarySlider'),
        polyVolumeSlider: document.getElementById('polyVolumeSlider'),

        presetsDrawer: document.getElementById('presetsDrawer'),
        openPresetsBtn: document.getElementById('openPresetsBtn'),
        closePresetsBtn: document.getElementById('closePresetsBtn'),
        presetsList: document.getElementById('presetsList'),
        newPresetName: document.getElementById('newPresetName'),
        savePresetBtn: document.getElementById('savePresetBtn'),

        exportModal: document.getElementById('exportModal'),
        openExportBtn: document.getElementById('openExportBtn'),
        closeExportBtn: document.getElementById('closeExportBtn'),
        startExportBtn: document.getElementById('startExportBtn'),
        exportBars: document.getElementById('exportBars'),
        exportStatus: document.getElementById('exportStatus'),

        bouncingBallToggle: document.getElementById('bouncingBallToggle'),
        bouncingBallCanvas: document.getElementById('bouncingBallCanvas')
    };

    const parentSettingsDrawer = document.getElementById('settingsDrawer');

    [document.getElementById('presetsOverlay'), ui.presetsDrawer, document.getElementById('exportOverlay'), ui.exportModal].forEach((element) => {
        if (element && element.parentElement !== document.body) document.body.appendChild(element);
    });

    function getDialogFocusables(dialog) {
        return Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    }

    function setNestedDialogState(dialog, overlay, open, trigger, initialFocus) {
        if (!dialog) return;
        if (!open) window.FLToolDialogIsolation?.close(dialog);
        dialog.classList.toggle('open', open);
        dialog.setAttribute('aria-hidden', String(!open));
        dialog.toggleAttribute('inert', !open);
        if (overlay) {
            overlay.classList.toggle('open', open);
            overlay.setAttribute('aria-hidden', String(!open));
        }
        if (parentSettingsDrawer?.classList.contains('open')) {
            parentSettingsDrawer.setAttribute('aria-hidden', String(open));
            parentSettingsDrawer.toggleAttribute('inert', open);
        }
        if (open) {
            window.FLToolDialogIsolation?.open(dialog, [overlay]);
            requestAnimationFrame(() => initialFocus?.focus());
        } else {
            if (parentSettingsDrawer?.classList.contains('open')) {
                window.FLToolDialogIsolation?.open(parentSettingsDrawer, [document.getElementById('settingsOverlay')]);
            }
            requestAnimationFrame(() => trigger?.focus());
        }
    }

    function trapDialogFocus(event, dialog) {
        if (event.key !== 'Tab' || !dialog?.classList.contains('open')) return;
        const focusables = getDialogFocusables(dialog);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    let beatDotsCache = [];
    let subdivCellsCache = [];

    function refreshPulseCaches() {
        beatDotsCache = ui.beatDots ? Array.from(ui.beatDots.querySelectorAll('.beat-dot')) : [];
        subdivCellsCache = ui.subdivSelector ? Array.from(ui.subdivSelector.querySelectorAll('.subdiv-cell')) : [];
    }

    // ---- HELPERS ----
    function updateBpmUI() {
        if (ui.bpmDisplay) {
            // Only update text when not actively being edited
            if (document.activeElement !== ui.bpmDisplay) {
                ui.bpmDisplay.innerText = engine.bpm;
            }
            ui.bpmDisplay.setAttribute('aria-valuenow', engine.bpm);
        }
        if (ui.bpmSlider) ui.bpmSlider.value = engine.bpm;
        if (ui.tempoMarking) ui.tempoMarking.textContent = MetronomeEngine.getTempoMarking(engine.bpm);

        document.querySelectorAll('.preset-btn').forEach(btn => {
            const btnBpm = parseInt(btn.getAttribute('data-bpm'));
            btn.classList.toggle('active', btnBpm === engine.bpm);
        });
    }

    function updateBeatDots() {
        if (!ui.beatDots) return;
        ui.beatDots.innerHTML = '';
        for (let i = 0; i < engine.beatsPerBar; i++) {
            const dot = document.createElement('div');
            dot.className = 'beat-dot';
            ui.beatDots.appendChild(dot);
        }
        refreshPulseCaches();
    }

    let updateTimeSig = function () {
        if (ui.beatsPerBar) ui.beatsPerBar.innerText = engine.beatsPerBar;
        if (engine.beatsPerBar !== 4 && engine.jazzMode) {
            engine.jazzMode = false;
            if (ui.jazzMode) ui.jazzMode.checked = false;
        }
        engine.syncBeatSubdivisions();
        updateJazzIcon();
        updateBeatDots();
        buildSubdivSelector();
        buildAccentGrid();
        updateClaveVisual();
        buildSubdivGrid();
    }

    function updateJazzIcon() {
        if (!ui.jazzIcon) return;
        const isAvailable = engine.beatsPerBar === 4;
        ui.jazzIcon.hidden = !isAvailable;
        ui.jazzIcon.disabled = !isAvailable;
        if (isAvailable) {
            ui.jazzIcon.classList.add('visible');
        } else {
            ui.jazzIcon.classList.remove('visible');
        }
        ui.jazzIcon.classList.toggle('active', engine.jazzMode);
        ui.jazzIcon.setAttribute('aria-pressed', engine.jazzMode ? 'true' : 'false');
    }

    function formatTime(sec) {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    function updateStats() {
        const s = engine.stats;
        if (ui.statBeats) ui.statBeats.textContent = s.totalBeats.toLocaleString();
        if (ui.statTime) ui.statTime.textContent = formatTime(s.totalSeconds);
        if (ui.statSessions) ui.statSessions.textContent = s.sessions;
    }

    // ===== SUBDIVISION SELECTOR BUILDER =====
    function buildSubdivSelector() {
        if (!ui.subdivSelector) return;
        ui.subdivSelector.innerHTML = '';
        engine.syncBeatSubdivisions();

        for (let i = 0; i < engine.beatsPerBar; i++) {
            const cell = document.createElement('button');
            cell.type = 'button';
            cell.className = 'subdiv-cell';
            cell.setAttribute('data-beat', i);
            const subdivCount = engine.beatSubdivisions[i];
            const info = engine.subdivisionInfo[subdivCount];
            cell.textContent = subdivCount === 1 ? '–' : subdivCount;
            cell.style.setProperty('--subdiv-color', info.color);
            if (subdivCount > 1) cell.classList.add('has-subdiv');
            cell.title = info.label;
            cell.setAttribute('aria-label', `Beat ${i + 1} subdivision: ${info.label}`);

            // Tap to cycle for this beat only
            cell.addEventListener('click', (e) => {
                e.preventDefault();

                const newVal = engine.cycleSubdivisionForBeat(i);
                const newInfo = engine.subdivisionInfo[newVal];
                cell.textContent = newVal === 1 ? '–' : newVal;
                cell.style.setProperty('--subdiv-color', newInfo.color);
                cell.classList.toggle('has-subdiv', newVal > 1);
                cell.title = newInfo.label;
                cell.setAttribute('aria-label', `Beat ${i + 1} subdivision: ${newInfo.label}`);
                updateSubdivLegend();

                // Micro-animation
                cell.style.transform = 'scale(1.2)';
                setTimeout(() => { cell.style.transform = ''; }, 150);
            });

            // Long press to reset to 1
            let pressTimer;
            cell.addEventListener('touchstart', () => {
                pressTimer = setTimeout(() => {
                    engine.setSubdivisionForBeat(i, 1);
                    cell.textContent = '–';
                    cell.style.setProperty('--subdiv-color', engine.subdivisionInfo[1].color);
                    cell.classList.remove('has-subdiv');
                    cell.title = 'Quarter';
                    cell.setAttribute('aria-label', `Beat ${i + 1} subdivision: Quarter`);
                    updateSubdivLegend();
                    if (navigator.vibrate) navigator.vibrate(15);
                }, 500);
            }, { passive: true });
            cell.addEventListener('touchend', () => clearTimeout(pressTimer));
            cell.addEventListener('touchcancel', () => clearTimeout(pressTimer));

            ui.subdivSelector.appendChild(cell);
        }

        updateSubdivLegend();
        refreshPulseCaches();
    }

    function updateSubdivLegend() {
        if (!ui.subdivLegend) return;
        // Show the active subdivisions as a legend
        const unique = [...new Set(engine.beatSubdivisions.filter(s => s > 1))];
        if (unique.length === 0) {
            ui.subdivLegend.innerHTML = '<span class="subdiv-legend-item">tap numbers to add subdivisions</span>';
        } else {
            const labels = unique.map(s => {
                const info = engine.subdivisionInfo[s];
                return `<span class="subdiv-legend-chip" style="--chip-color:${info.color}">${info.label}</span>`;
            });
            ui.subdivLegend.innerHTML = labels.join('');
        }
    }

    // ---- ACCENT GRID BUILDER ----
    function buildAccentGrid() {
        if (!ui.accentGrid) return;
        ui.accentGrid.innerHTML = '';

        while (engine.customAccents.length < engine.beatsPerBar) engine.customAccents.push(false);
        engine.customAccents.length = engine.beatsPerBar;

        for (let i = 0; i < engine.beatsPerBar; i++) {
            const cell = document.createElement('button');
            cell.type = 'button';
            cell.className = 'accent-cell' + (engine.customAccents[i] ? ' active' : '');
            cell.textContent = i + 1;
            cell.setAttribute('aria-label', `Accent beat ${i + 1}`);
            cell.setAttribute('aria-pressed', String(engine.customAccents[i]));
            cell.addEventListener('click', () => {
                engine.customAccents[i] = !engine.customAccents[i];
                cell.classList.toggle('active', engine.customAccents[i]);
                cell.setAttribute('aria-pressed', String(engine.customAccents[i]));
                engine.accentMode = 'custom';
                if (ui.accentSelect) ui.accentSelect.value = 'custom';
            });
            ui.accentGrid.appendChild(cell);
        }
    }

    // ---- CLAVE VISUAL ----
    function updateClaveVisual() {
        if (!ui.claveVisual) return;
        const patDef = engine.clavePatterns[engine.clavePattern];
        if (!patDef) return;

        ui.claveVisual.innerHTML = '';
        const { hits, cycleLen, label } = patDef;

        for (let i = 0; i < cycleLen; i++) {
            const slot = document.createElement('div');
            slot.className = 'clave-slot';
            if (hits.includes(i)) slot.classList.add('hit');
            slot.setAttribute('data-pos', i);
            ui.claveVisual.appendChild(slot);
        }

        const labelEl = document.getElementById('claveLabel');
        if (labelEl) labelEl.textContent = label;
    }

    // ---- BUILD SUBDIVISION GRID ----
    function buildSubdivGrid() {
        if (!ui.subdivGrid) return;
        ui.subdivGrid.innerHTML = '';

        for (let beat = 0; beat < engine.beatsPerBar; beat++) {
            const col = document.createElement('div');
            col.className = 'grid-beat-col';
            col.setAttribute('data-beat', beat);

            ['1', '&', 'e', 'a'].forEach((label, idx) => {
                const cell = document.createElement('div');
                cell.className = 'grid-cell';
                cell.setAttribute('data-beat', beat);
                cell.setAttribute('data-sub', idx);
                cell.textContent = idx === 0 ? (beat + 1) : label;
                col.appendChild(cell);
            });

            ui.subdivGrid.appendChild(col);
        }
    }


    // ---- EVENT LISTENERS ----

    // ---- STATUS ANNOUNCER ----
    const metroStatus = document.getElementById('metroStatus');
    function announce(msg) {
        if (!metroStatus) return;
        metroStatus.textContent = '';
        // Flush before setting so repeated identical messages still fire
        requestAnimationFrame(() => { metroStatus.textContent = msg; });
    }

    // Play/Stop
    ui.playBtn?.addEventListener('click', () => {
        engine.toggle();
        ui.playBtn.classList.toggle('active', engine.isPlaying);
        const iconPlay = ui.playBtn.querySelector('.icon-play');
        const iconStop = ui.playBtn.querySelector('.icon-stop');
        if (iconPlay) iconPlay.style.display = engine.isPlaying ? 'none' : 'block';
        if (iconStop) iconStop.style.display = engine.isPlaying ? 'block' : 'none';
        ui.playBtn.setAttribute('aria-label', engine.isPlaying ? 'Stop metronome' : 'Start metronome');
        ui.playBtn.setAttribute('aria-pressed', String(engine.isPlaying));
        announce(engine.isPlaying ? `Metronome started at ${engine.bpm} BPM` : 'Metronome stopped');
    });

    // BPM
    ui.bpmSlider?.addEventListener('input', (e) => { engine.setBpm(parseInt(e.target.value)); updateBpmUI(); });
    ui.bpmPlus?.addEventListener('click', () => { engine.setBpm(engine.bpm + 1); updateBpmUI(); });
    ui.bpmMinus?.addEventListener('click', () => { engine.setBpm(engine.bpm - 1); updateBpmUI(); });

    // BPM display — contenteditable direct entry
    if (ui.bpmDisplay) {
        // Select all text on focus for easy overwrite
        ui.bpmDisplay.addEventListener('focus', () => {
            const sel = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(ui.bpmDisplay);
            sel.removeAllRanges();
            sel.addRange(range);
        });

        // Intercept paste — strip non-digits (handles Firefox plaintext-only fallback)
        ui.bpmDisplay.addEventListener('paste', (e) => {
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text/plain').replace(/[^\d]/g, '');
            if (text) document.execCommand('insertText', false, text.slice(0, 3));
        });

        // Block non-numeric input (allow digits + navigation keys)
        ui.bpmDisplay.addEventListener('keydown', (e) => {
            const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'];
            if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
                e.preventDefault();
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                ui.bpmDisplay.blur();
            }
            if (e.key === 'Escape') {
                ui.bpmDisplay.innerText = engine.bpm;
                ui.bpmDisplay.blur();
            }
            // Arrow keys: nudge BPM (consistent with keyboard shortcuts, only when not editing text position)
            if (e.key === 'ArrowUp' && ui.bpmDisplay.innerText.length <= 3) {
                e.preventDefault();
                engine.setBpm(engine.bpm + (e.shiftKey ? 5 : 1));
                updateBpmUI();
            }
            if (e.key === 'ArrowDown' && ui.bpmDisplay.innerText.length <= 3) {
                e.preventDefault();
                engine.setBpm(engine.bpm - (e.shiftKey ? 5 : 1));
                updateBpmUI();
            }
        });

        // Commit on blur
        ui.bpmDisplay.addEventListener('blur', () => {
            const raw = parseInt(ui.bpmDisplay.innerText.trim().replace(/[^\d]/g, ''), 10);
            if (!isNaN(raw)) {
                engine.setBpm(raw);
            }
            // Always restore canonical value
            ui.bpmDisplay.innerText = engine.bpm;
            ui.bpmDisplay.setAttribute('aria-valuenow', engine.bpm);
            if (ui.bpmSlider) ui.bpmSlider.value = engine.bpm;
            if (ui.tempoMarking) ui.tempoMarking.textContent = MetronomeEngine.getTempoMarking(engine.bpm);
            document.querySelectorAll('.preset-btn').forEach(btn => {
                btn.classList.toggle('active', parseInt(btn.getAttribute('data-bpm')) === engine.bpm);
            });
        });
    }

    // Tap Tempo
    let taps = [];
    ui.tapBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        const now = Date.now();
        if (taps.length > 0 && now - taps[taps.length - 1] > 2000) taps = [];
        taps.push(now);
        if (taps.length > 4) taps.shift();

        // Visual feedback
        ui.tapBtn.style.transform = 'scale(0.92)';
        ui.tapBtn.style.background = 'var(--m-accent)';
        ui.tapBtn.style.color = '#000';
        setTimeout(() => {
            ui.tapBtn.style.transform = '';
            ui.tapBtn.style.background = '';
            ui.tapBtn.style.color = '';
        }, 120);

        if (taps.length > 2) {
            const intervals = [];
            for (let i = 1; i < taps.length; i++) intervals.push(taps[i] - taps[i - 1]);
            const avg = intervals.reduce((a, b) => a + b) / intervals.length;
            const newBpm = Math.round(60000 / avg);
            if (newBpm >= 30 && newBpm <= 300) { engine.setBpm(newBpm); updateBpmUI(); }
        }
    });

    // Mixer faders
    const mixerMap = {
        'volQuarter': 'quarter', 'volEighth': 'eighth', 'volSixteenth': 'sixteenth',
        'volTriplet': 'triplet', 'volQuintuplet': 'quintuplet', 'volSextuplet': 'sextuplet',
        'volSeptuplet': 'septuplet', 'volThirtySecond': 'thirtySecond'
    };
    Object.entries(mixerMap).forEach(([elId, volKey]) => {
        const el = ui[elId];
        if (el) {
            el.addEventListener('input', (e) => {
                engine.volumes[volKey] = parseFloat(e.target.value);
                // If user touches mixer, switch to mixer mode
                if (parseFloat(e.target.value) > 0) {
                    engine.usePerBeatMode = false;
                }
            });
        }
    });

    // Humanize
    ui.humanize?.addEventListener('input', (e) => {
        const val = parseFloat(e.target.value);
        engine.humanizeFactor = val;
        let text = 'None';
        if (val > 0.1) text = 'Light';
        if (val > 0.4) text = 'Natural';
        if (val > 0.7) text = 'Loose';
        if (ui.humanizeVal) ui.humanizeVal.innerText = text;
    });

    // Jazz Mode (toggle via settings)
    ui.jazzMode?.addEventListener('change', (e) => {
        engine.jazzMode = e.target.checked;
        if (engine.jazzMode && engine.beatsPerBar !== 4) {
            engine.beatsPerBar = 4;
            updateTimeSig();
        }
        updateJazzIcon();
    });

    // Jazz Mode (toggle via sax icon)
    ui.jazzIcon?.addEventListener('click', () => {
        if (engine.beatsPerBar !== 4) return;
        engine.jazzMode = !engine.jazzMode;
        if (ui.jazzMode) ui.jazzMode.checked = engine.jazzMode;
        updateJazzIcon();
    });

    // Sound Bank
    ui.soundBank?.addEventListener('change', (e) => engine.soundBank = e.target.value);

    // Haptics
    ui.haptics?.addEventListener('change', (e) => engine.useHaptics = e.target.checked);

    // Swing
    ui.swingSlider?.addEventListener('input', (e) => {
        const val = parseInt(e.target.value);
        engine.swingAmount = val;
        let label = `${val}%`;
        if (val <= 51) label += ' (straight)';
        else if (val >= 65 && val <= 69) label += ' (triplet)';
        else if (val > 69) label += ' (heavy)';
        else label += ' (light swing)';
        if (ui.swingValue) ui.swingValue.textContent = label;
    });

    // Accent Strength
    ui.accentStrengthSlider?.addEventListener('input', (e) => {
        const val = parseFloat(e.target.value);
        engine.accentStrength = val;
        if (ui.accentStrengthValue) ui.accentStrengthValue.textContent = Math.round(val * 100) + '%';
    });

    // Screen Flash
    ui.screenFlashToggle?.addEventListener('change', (e) => {
        engine.screenFlash = e.target.checked;
    });

    // Count-In
    ui.countInToggle?.addEventListener('change', (e) => {
        engine.countIn.enabled = e.target.checked;
        if (ui.countInBadge) ui.countInBadge.style.display = e.target.checked ? 'block' : 'none';
    });

    // Practice Mode
    ui.practiceModeToggle?.addEventListener('change', (e) => {
        engine.practiceMode.enabled = e.target.checked;
        const controls = document.getElementById('practiceModeControls');
        if (controls) controls.style.display = e.target.checked ? 'block' : 'none';
    });
    ui.playBarsSlider?.addEventListener('input', (e) => {
        engine.practiceMode.playBars = parseInt(e.target.value);
        const lbl = document.getElementById('playBarsLabel');
        if (lbl) lbl.textContent = e.target.value;
    });
    ui.muteBarsSlider?.addEventListener('input', (e) => {
        engine.practiceMode.muteBars = parseInt(e.target.value);
        const lbl = document.getElementById('muteBarsLabel');
        if (lbl) lbl.textContent = e.target.value;
    });

    // ========================================
    // V7 FEATURE LISTENERS
    // ========================================

    // ---- POLYRHYTHM ----
    ui.polyToggle?.addEventListener('change', (e) => {
        engine.polyrhythm.enabled = e.target.checked;
        if (ui.polyControls) ui.polyControls.style.display = e.target.checked ? 'block' : 'none';
    });

    ui.polySecondarySlider?.addEventListener('input', (e) => {
        const val = parseInt(e.target.value);
        engine.polyrhythm.secondary = val;
        if (ui.polySecondaryDisp) ui.polySecondaryDisp.textContent = val;
    });

    ui.polyVolumeSlider?.addEventListener('input', (e) => {
        engine.polyrhythm.vol = parseFloat(e.target.value);
    });

    // Update primary display when time sig changes
    const _origUpdate = updateTimeSig;
    updateTimeSig = function () {
        _origUpdate();
        if (ui.polyPrimaryDisp) ui.polyPrimaryDisp.textContent = engine.beatsPerBar;
        engine.polyrhythm.primary = engine.beatsPerBar;
    };


    // ---- BOUNCING BALL ----
    let ballCtx = null;
    let ballReqId = null;
    let lastBeatVisualAt = 0;
    let beatDurationMs = 60000 / engine.bpm;

    if (ui.bouncingBallCanvas) {
        ballCtx = ui.bouncingBallCanvas.getContext('2d');
        ui.bouncingBallCanvas.width = 300;
        ui.bouncingBallCanvas.height = 100;
    }

    ui.bouncingBallToggle?.addEventListener('change', (e) => {
        engine.bouncingBall = e.target.checked;
        if (engine.bouncingBall) {
            if (ui.bouncingBallCanvas) ui.bouncingBallCanvas.style.display = 'block';
            startBallLoop();
        } else {
            if (ui.bouncingBallCanvas) ui.bouncingBallCanvas.style.display = 'none';
            stopBallLoop();
        }
    });

    function startBallLoop() {
        if (lastBeatVisualAt === 0) lastBeatVisualAt = performance.now();
        if (!ballReqId && ballCtx) renderBall();
    }

    function stopBallLoop() {
        if (ballReqId) { cancelAnimationFrame(ballReqId); ballReqId = null; }
        if (ballCtx) ballCtx.clearRect(0, 0, 300, 100);
        lastBeatVisualAt = 0;
    }

    function renderBall() {
        if (!engine.bouncingBall) {
            stopBallLoop();
            return;
        }

        if (!engine.isPlaying) {
            if (ballCtx) ballCtx.clearRect(0, 0, 300, 100);
            ballReqId = null;
            return;
        }

        const now = performance.now();
        const total = Math.max(16, beatDurationMs || (60000 / Math.max(30, engine.bpm)));
        const elapsed = now - lastBeatVisualAt;
        const progress = Math.min(1, Math.max(0, elapsed / total));

        // Parabolic arc for bounce
        const w = 300;
        const h = 100;
        ballCtx.clearRect(0, 0, w, h);

        // Simple center bounce
        const bounceHeight = Math.sin(progress * Math.PI) * 80;
        const y = h - 10 - bounceHeight;
        const centerX = w / 2;

        ballCtx.fillStyle = '#f97316';
        ballCtx.beginPath();
        ballCtx.arc(centerX, y, 10, 0, Math.PI * 2);
        ballCtx.fill();

        // Shadow
        ballCtx.fillStyle = 'rgba(0,0,0,0.2)';
        ballCtx.beginPath();
        const shadowSize = 10 + (bounceHeight / 4);
        const shadowOpacity = 0.5 - (bounceHeight / 200);
        ballCtx.globalAlpha = Math.max(0, shadowOpacity);
        ballCtx.ellipse(centerX, h - 5, shadowSize, shadowSize / 3, 0, 0, Math.PI * 2);
        ballCtx.fill();
        ballCtx.globalAlpha = 1;

        ballReqId = requestAnimationFrame(renderBall);
    }


    // ---- PRESETS ----
    function renderPresets() {
        if (!ui.presetsList) return;
        ui.presetsList.innerHTML = '';
        if (engine.presets.length === 0) {
            ui.presetsList.innerHTML = '<div class="empty-state">No saved presets yet.</div>';
            return;
        }
        engine.presets.forEach(p => {
            const el = document.createElement('div');
            el.className = 'preset-item';
            const loadButton = document.createElement('button');
            loadButton.type = 'button';
            loadButton.className = 'preset-load';
            loadButton.setAttribute('aria-label', `Load preset ${p.name}`);
            const info = document.createElement('span');
            info.className = 'preset-info';
            const name = document.createElement('span');
            name.className = 'preset-name';
            name.textContent = p.name;
            const meta = document.createElement('span');
            meta.className = 'preset-meta';
            meta.textContent = `${p.bpm} BPM · ${p.beatsPerBar}/4`;
            info.append(name, meta);
            loadButton.appendChild(info);

            const deleteButton = document.createElement('button');
            deleteButton.type = 'button';
            deleteButton.className = 'preset-delete';
            deleteButton.dataset.id = p.id;
            deleteButton.setAttribute('aria-label', `Delete preset ${p.name}`);
            deleteButton.textContent = 'Delete';

            loadButton.addEventListener('click', () => {
                engine.loadPreset(p.id);
                el.style.borderColor = 'var(--m-green)';
                setTimeout(() => el.style.borderColor = '', 300);
            });
            deleteButton.addEventListener('click', () => {
                if (confirm('Delete preset "' + p.name + '"?')) {
                    engine.deletePreset(p.id);
                    renderPresets();
                }
            });
            el.append(loadButton, deleteButton);
            ui.presetsList.appendChild(el);
        });
    }

    const presetsOverlay = document.getElementById('presetsOverlay');

    function openPresetsDrawer() {
        renderPresets();
        setNestedDialogState(ui.presetsDrawer, presetsOverlay, true, ui.openPresetsBtn, ui.closePresetsBtn);
    }
    function closePresetsDrawer() {
        setNestedDialogState(ui.presetsDrawer, presetsOverlay, false, ui.openPresetsBtn);
    }
    ui.openPresetsBtn?.addEventListener('click', openPresetsDrawer);
    ui.closePresetsBtn?.addEventListener('click', closePresetsDrawer);
    presetsOverlay?.addEventListener('click', closePresetsDrawer);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && ui.presetsDrawer?.classList.contains('open')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            closePresetsDrawer();
        }
        trapDialogFocus(e, ui.presetsDrawer);
    });

    ui.savePresetBtn?.addEventListener('click', () => {
        const name = ui.newPresetName.value.trim();
        if (!name) return alert('Please enter a name');
        engine.addPreset(name);
        ui.newPresetName.value = '';
        renderPresets();
    });

    window.addEventListener('metronome:presetLoaded', (e) => {
        // Refresh all UI
        ui.bpmSlider.value = engine.bpm;
        updateBpmUI();
        updateTimeSig(); // handles beatsPerBar, jazzMode, grid
        // updateSliders...
        Object.entries(mixerMap).forEach(([elId, volKey]) => {
            if (ui[elId]) ui[elId].value = engine.volumes[volKey];
        });
        if (ui.humanize) ui.humanize.value = engine.humanizeFactor;
        if (ui.swingSlider) ui.swingSlider.value = engine.swingAmount;
        if (ui.soundBank) ui.soundBank.value = engine.soundBank;

        // V7 specific refreshes
        if (ui.polyToggle) ui.polyToggle.checked = engine.polyrhythm.enabled;
        if (ui.polyControls) ui.polyControls.style.display = engine.polyrhythm.enabled ? 'block' : 'none';
        if (ui.polySecondarySlider) ui.polySecondarySlider.value = engine.polyrhythm.secondary;
        if (ui.polyVolumeSlider) ui.polyVolumeSlider.value = engine.polyrhythm.vol;
        if (ui.polySecondaryDisp) ui.polySecondaryDisp.textContent = engine.polyrhythm.secondary;
    });


    // ---- EXPORT ----
    const exportOverlay = document.getElementById('exportOverlay');

    function openExportModal() {
        setNestedDialogState(ui.exportModal, exportOverlay, true, ui.openExportBtn, ui.closeExportBtn);
    }
    function closeExportModal() {
        setNestedDialogState(ui.exportModal, exportOverlay, false, ui.openExportBtn);
    }
    ui.openExportBtn?.addEventListener('click', openExportModal);
    ui.closeExportBtn?.addEventListener('click', closeExportModal);
    exportOverlay?.addEventListener('click', closeExportModal);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && ui.exportModal?.classList.contains('open')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            closeExportModal();
        }
        trapDialogFocus(e, ui.exportModal);
    });

    ui.startExportBtn?.addEventListener('click', async () => {
        ui.startExportBtn.disabled = true;
        ui.startExportBtn.textContent = 'Rendering...';
        ui.exportStatus.style.display = 'block';

        const bars = parseInt(ui.exportBars.value) || 8;

        try {
            const buffer = await engine.renderAudioExport(bars);
            if (!buffer) throw new Error('Export not supported or failed');

            // Encode to WAV (Simple helper)
            const wavData = audioBufferToWav(buffer);
            const blob = new Blob([wavData], { type: 'audio/wav' });
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `Metronome_${engine.bpm}BPM.wav`;
            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 100);

        } catch (e) {
            console.error(e);
            alert('Export failed: ' + e.message);
        }

        ui.startExportBtn.disabled = false;
        ui.startExportBtn.textContent = 'Download WAV';
        ui.exportStatus.style.display = 'none';
    });

    // WAV Encoder Helper (interleaved 16-bit PCM)
    function audioBufferToWav(buffer) {
        const numChannels = buffer.numberOfChannels;
        const sampleRate = buffer.sampleRate;
        const format = 1; // PCM
        const bitDepth = 16;

        let result;
        if (numChannels === 2) {
            result = interleave(buffer.getChannelData(0), buffer.getChannelData(1));
        } else {
            result = buffer.getChannelData(0);
        }

        return encodeWAV(result, numChannels, sampleRate, bitDepth);
    }

    function interleave(inputL, inputR) {
        const length = inputL.length + inputR.length;
        const result = new Float32Array(length);
        let index = 0;
        let inputIndex = 0;
        while (index < length) {
            result[index++] = inputL[inputIndex];
            result[index++] = inputR[inputIndex];
            inputIndex++;
        }
        return result;
    }

    function encodeWAV(samples, numChannels, sampleRate, bitDepth) {
        const bytesPerSample = bitDepth / 8;
        const blockAlign = numChannels * bytesPerSample;
        const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
        const view = new DataView(buffer);

        writeString(view, 0, 'RIFF');
        view.setUint32(4, 36 + samples.length * bytesPerSample, true);
        writeString(view, 8, 'WAVE');
        writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * blockAlign, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, bitDepth, true);
        writeString(view, 36, 'data');
        view.setUint32(40, samples.length * bytesPerSample, true);

        if (bitDepth === 16) {
            floatTo16BitPCM(view, 44, samples);
        } else {
            writeFloat32(view, 44, samples);
        }
        return view;
    }

    function floatTo16BitPCM(output, offset, input) {
        for (let i = 0; i < input.length; i++, offset += 2) {
            const s = Math.max(-1, Math.min(1, input[i]));
            output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
    }

    function writeFloat32(output, offset, input) {
        for (let i = 0; i < input.length; i++, offset += 4) {
            output.setFloat32(offset, input[i], true);
        }
    }

    function writeString(view, offset, string) {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    }

    // Master Volume
    const masterVolSlider = document.getElementById('masterVolume');
    const masterVolLabel = document.getElementById('masterVolLabel');
    if (masterVolSlider) {
        masterVolSlider.addEventListener('input', (e) => {
            const val = parseFloat(e.target.value);
            engine.setMasterVolume(val);
            if (masterVolLabel) masterVolLabel.textContent = Math.round(val * 100) + '%';
        });
    }

    // Time Sig
    ui.timeSigPlus?.addEventListener('click', () => {
        if (engine.beatsPerBar < 12) { engine.beatsPerBar++; updateTimeSig(); }
    });
    ui.timeSigMinus?.addEventListener('click', () => {
        if (engine.beatsPerBar > 1) { engine.beatsPerBar--; updateTimeSig(); }
    });

    // Clave
    ui.claveToggle?.addEventListener('change', (e) => {
        engine.claveEnabled = e.target.checked;
        updateClaveVisual();
    });
    ui.claveSelect?.addEventListener('change', (e) => {
        engine.clavePattern = e.target.value;
        updateClaveVisual();
    });
    ui.claveVolume?.addEventListener('input', (e) => {
        engine.claveVolume = parseFloat(e.target.value);
    });

    // Accent
    ui.accentSelect?.addEventListener('change', (e) => {
        engine.accentMode = e.target.value;
        if (e.target.value === 'custom') buildAccentGrid();
    });

    // Speed Trainer
    ui.trainerToggle?.addEventListener('change', (e) => {
        engine.speedTrainer.enabled = e.target.checked;
        const trainerControls = document.getElementById('trainerControls');
        if (trainerControls) trainerControls.style.display = e.target.checked ? 'block' : 'none';
    });
    ui.trainerStart?.addEventListener('input', (e) => {
        engine.speedTrainer.startBpm = parseInt(e.target.value);
        const lbl = document.getElementById('trainerStartLabel');
        if (lbl) lbl.textContent = e.target.value;
    });
    ui.trainerTarget?.addEventListener('input', (e) => {
        engine.speedTrainer.targetBpm = parseInt(e.target.value);
        const lbl = document.getElementById('trainerTargetLabel');
        if (lbl) lbl.textContent = e.target.value;
    });
    ui.trainerStep?.addEventListener('input', (e) => {
        engine.speedTrainer.stepBpm = parseInt(e.target.value);
        const lbl = document.getElementById('trainerStepLabel');
        if (lbl) lbl.textContent = e.target.value + ' BPM';
    });
    ui.trainerBars?.addEventListener('input', (e) => {
        engine.speedTrainer.barsPerStep = parseInt(e.target.value);
        const lbl = document.getElementById('trainerBarsLabel');
        if (lbl) lbl.textContent = e.target.value;
    });

    // ---- VISUAL EVENT HOOKS ----
    const ringPulseClasses = ['pulse-downbeat', 'pulse-beat', 'pulse-jazz', 'pulse-accent'];

    function triggerRingPulse(pulseClass) {
        if (!ui.ring) return;
        ui.ring.classList.remove(...ringPulseClasses);
        if (ui._ringPulseRaf) cancelAnimationFrame(ui._ringPulseRaf);
        ui._ringPulseRaf = requestAnimationFrame(() => {
            ui.ring?.classList.add(pulseClass);
        });
        clearTimeout(ui._ringDecay);
        ui._ringDecay = setTimeout(() => {
            ui.ring?.classList.remove(...ringPulseClasses);
        }, Math.min(400, 60000 / engine.bpm * 0.7));
    }

    // Beat pulse
    window.addEventListener('metronome:beat', (e) => {
        const { beat, isDownbeat, isAccented, isJazzAccent } = e.detail;

        if (isJazzAccent) triggerRingPulse('pulse-jazz');
        else if (isAccented) triggerRingPulse('pulse-accent');
        else if (isDownbeat) triggerRingPulse('pulse-downbeat');
        else triggerRingPulse('pulse-beat');

        // Keep a reliable beat clock for the bouncing-ball visual.
        const beatNow = performance.now();
        const fallbackBeatMs = 60000 / Math.max(30, engine.bpm);
        if (lastBeatVisualAt > 0) {
            const delta = beatNow - lastBeatVisualAt;
            beatDurationMs = (delta > 20 && delta < 2000) ? delta : fallbackBeatMs;
        } else {
            beatDurationMs = fallbackBeatMs;
        }
        lastBeatVisualAt = beatNow;
        if (engine.bouncingBall && !e.detail.isMuted && !ballReqId) startBallLoop();

        // Beat dots
        beatDotsCache.forEach(d => d.classList.remove('active', 'accent'));
        if (beatDotsCache[beat]) {
            if (isDownbeat || isJazzAccent || isAccented) beatDotsCache[beat].classList.add('accent');
            else beatDotsCache[beat].classList.add('active');
        }

        // Subdivision selector pulse
        subdivCellsCache.forEach(c => c.classList.remove('playing'));
        if (subdivCellsCache[beat]) {
            subdivCellsCache[beat].classList.add('playing');
            // Dim during muted bars
            if (e.detail.isMuted) {
                subdivCellsCache[beat].style.opacity = '0.3';
                setTimeout(() => { subdivCellsCache[beat].style.opacity = ''; }, 200);
            }
        }

        // Count-in indicator
        if (e.detail.isCountingIn) {
            if (ui.countInBadge) {
                ui.countInBadge.textContent = engine.beatsPerBar - beat;
                ui.countInBadge.style.display = 'block';
            }
        } else if (ui.countInBadge && engine.countIn.enabled) {
            ui.countInBadge.style.display = 'none';
        }

        // Practice mode status
        if (e.detail.isMuted && ui.practiceStatus) {
            ui.practiceStatus.textContent = 'Muted — listen internally';
            ui.practiceStatus.classList.add('muted');
        } else if (ui.practiceStatus && engine.practiceMode.enabled) {
            ui.practiceStatus.textContent = 'Playing';
            ui.practiceStatus.classList.remove('muted');
        }

        // Total beats counter
        if (ui.totalBeatsDisp) ui.totalBeatsDisp.textContent = e.detail.totalBeats;
    });

    // BPM change from trainer
    window.addEventListener('metronome:bpmChange', (e) => {
        engine.bpm = e.detail.bpm;
        beatDurationMs = 60000 / Math.max(30, engine.bpm);
        updateBpmUI();
    });

    // Trainer progress
    window.addEventListener('metronome:trainerUpdate', (e) => {
        const d = e.detail;
        if (ui.trainerProgress) {
            ui.trainerProgress.style.width = (d.progress * 100) + '%';
        }
        if (ui.trainerBpmLabel) {
            ui.trainerBpmLabel.textContent = d.isComplete ? '✓ TARGET REACHED' : `${d.currentBpm} BPM`;
        }
    });

    // Timer tick
    window.addEventListener('metronome:timerTick', (e) => {
        const d = e.detail;
        const display = d.mode === 'countdown' ? formatTime(d.remaining) : formatTime(d.elapsed);
        if (ui.timerDisplay) ui.timerDisplay.textContent = display;
    });

    window.addEventListener('metronome:timerDone', () => {
        ui.playBtn?.classList.remove('active');
        const iconPlay = ui.playBtn?.querySelector('.icon-play');
        const iconStop = ui.playBtn?.querySelector('.icon-stop');
        if (iconPlay) iconPlay.style.display = 'block';
        if (iconStop) iconStop.style.display = 'none';
    });

    window.addEventListener('metronome:stop', () => {
        updateStats();
        stopBallLoop();
        clearTimeout(ui._ringDecay);
        if (ui._ringPulseRaf) cancelAnimationFrame(ui._ringPulseRaf);
        ui.ring?.classList.remove(...ringPulseClasses);
        // Clear playing states
        subdivCellsCache.forEach(c => c.classList.remove('playing'));
        beatDotsCache.forEach(d => d.classList.remove('active', 'accent'));
        // Sync play button UI
        if (ui.playBtn) {
            ui.playBtn.classList.remove('active');
            const iconPlay = ui.playBtn.querySelector('.icon-play');
            const iconStop = ui.playBtn.querySelector('.icon-stop');
            if (iconPlay) iconPlay.style.display = 'block';
            if (iconStop) iconStop.style.display = 'none';
        }
    });

    // Grid pulse
    window.addEventListener('metronome:gridPulse', (e) => {
        const { beat, position } = e.detail;
        let subIdx = 0;
        if (position === 0.25) subIdx = 2;
        else if (position === 0.5) subIdx = 1;
        else if (position === 0.75) subIdx = 3;
        else if (Math.abs(position - 1 / 3) < 0.01) subIdx = 1;
        else if (Math.abs(position - 2 / 3) < 0.01) subIdx = 3;

        const cell = ui.subdivGrid?.querySelector(`.grid-cell[data-beat="${beat}"][data-sub="${subIdx}"]`);
        if (cell) {
            cell.classList.add('lit');
            setTimeout(() => cell.classList.remove('lit'), Math.min(180, 60000 / engine.bpm * 0.4));
        }
    });

    // When per-beat subdivisions change, reactivate per-beat mode
    window.addEventListener('metronome:subdivChanged', () => {
        engine.usePerBeatMode = true;
        // Reset mixer faders to 0 (so they don't overlap)
        Object.keys(engine.volumes).forEach(k => {
            if (k !== 'quarter') engine.volumes[k] = 0;
        });
        // Update mixer UI
        Object.entries(mixerMap).forEach(([elId, volKey]) => {
            if (volKey !== 'quarter' && ui[elId]) ui[elId].value = 0;
        });
    });

    // Screen flash
    window.addEventListener('metronome:flash', () => {
        if (ui.screenFlashOverlay) {
            ui.screenFlashOverlay.classList.add('flash');
            setTimeout(() => ui.screenFlashOverlay.classList.remove('flash'), 120);
        }
    });

    // Practice mode toggle visual
    window.addEventListener('metronome:practiceToggle', (e) => {
        const app = document.querySelector('.metronome-app');
        if (app) {
            app.classList.toggle('practice-muted', e.detail.isMuted);
        }
    });

    // Count-in done
    window.addEventListener('metronome:countInDone', () => {
        if (ui.countInBadge) {
            ui.countInBadge.textContent = '▶';
            setTimeout(() => { if (engine.countIn.enabled) ui.countInBadge.style.display = 'none'; }, 500);
        }
    });

    // ---- TAB SWITCHING ----
    const settingsTabs = Array.from(document.querySelectorAll('.panel-tabs .tab-btn'));
    settingsTabs.forEach(btn => {
        const activateTab = () => {
            settingsTabs.forEach(b => {
                b.classList.remove('active');
                b.setAttribute('aria-selected', 'false');
                b.setAttribute('tabindex', '-1');
            });
            document.querySelectorAll('.secondary-panel .tab-content').forEach(c => {
                c.classList.remove('active');
                c.setAttribute('aria-hidden', 'true');
            });
            btn.classList.add('active');
            btn.setAttribute('aria-selected', 'true');
            btn.setAttribute('tabindex', '0');
            const tabId = btn.getAttribute('data-tab');
            const content = document.getElementById(`tab-${tabId}`);
            if (content) {
                content.classList.add('active');
                content.setAttribute('aria-hidden', 'false');
            }
        };
        btn.addEventListener('click', activateTab);
        btn.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const index = settingsTabs.indexOf(btn);
            let next = index;
            if (event.key === 'ArrowLeft') next = (index - 1 + settingsTabs.length) % settingsTabs.length;
            if (event.key === 'ArrowRight') next = (index + 1) % settingsTabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = settingsTabs.length - 1;
            settingsTabs[next].focus();
            settingsTabs[next].click();
        });
    });

    // Preset Buttons
    document.querySelectorAll('.preset-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const bpm = parseInt(btn.getAttribute('data-bpm'));
            engine.setBpm(bpm);
            updateBpmUI();
        });
    });

    // ---- KEYBOARD SHORTCUTS ----
    document.addEventListener('keydown', (e) => {
        if (ui.presetsDrawer?.classList.contains('open') || ui.exportModal?.classList.contains('open') || parentSettingsDrawer?.classList.contains('open')) return;
        if (e.target.closest?.('input, select, textarea, button, a, [contenteditable="true"], [role="button"]')) return;

        switch (e.code) {
            case 'Space': {
                e.preventDefault();
                engine.toggle();
                ui.playBtn?.classList.toggle('active', engine.isPlaying);
                const iconPlay = ui.playBtn?.querySelector('.icon-play');
                const iconStop = ui.playBtn?.querySelector('.icon-stop');
                if (iconPlay) iconPlay.style.display = engine.isPlaying ? 'none' : 'block';
                if (iconStop) iconStop.style.display = engine.isPlaying ? 'block' : 'none';
                announce(engine.isPlaying ? `Metronome started at ${engine.bpm} BPM` : 'Metronome stopped');
                break;
            }
            case 'ArrowUp':
                e.preventDefault();
                engine.setBpm(engine.bpm + (e.shiftKey ? 5 : 1));
                updateBpmUI();
                break;
            case 'ArrowDown':
                e.preventDefault();
                engine.setBpm(engine.bpm - (e.shiftKey ? 5 : 1));
                updateBpmUI();
                break;
            case 'KeyT':
                ui.tapBtn?.click();
                break;
            case 'KeyJ':
                if (engine.beatsPerBar === 4) {
                    engine.jazzMode = !engine.jazzMode;
                    if (ui.jazzMode) ui.jazzMode.checked = engine.jazzMode;
                    updateJazzIcon();
                }
                break;
            case 'KeyC':
                if (ui.countInToggle) {
                    ui.countInToggle.checked = !ui.countInToggle.checked;
                    ui.countInToggle.dispatchEvent(new Event('change'));
                }
                break;
            case 'KeyM':
                if (ui.practiceModeToggle) {
                    ui.practiceModeToggle.checked = !ui.practiceModeToggle.checked;
                    ui.practiceModeToggle.dispatchEvent(new Event('change'));
                }
                break;
        }
    });

    // ---- SETTINGS PERSISTENCE ----
    const STORAGE_KEY = 'sa-tool-metronome';

    function saveSettings() {
        try {
            const s = {
                bpm: engine.bpm,
                beatsPerBar: engine.beatsPerBar,
                beatSubdivisions: [...engine.beatSubdivisions],
                soundBank: engine.soundBank,
                swingAmount: engine.swingAmount,
                humanizeFactor: engine.humanizeFactor,
                accentStrength: engine.accentStrength,
                jazzMode: engine.jazzMode,
                screenFlash: engine.screenFlash,
                useHaptics: engine.useHaptics,
                claveEnabled: engine.claveEnabled,
                clavePattern: engine.clavePattern,
                claveVolume: engine.claveVolume,
                accentMode: engine.accentMode,
                masterVolume: engine.masterGain ? engine.masterGain.gain.value : 0.8,
                countInEnabled: engine.countIn.enabled,
                practiceModeEnabled: engine.practiceMode.enabled,
                practicePlayBars: engine.practiceMode.playBars,
                practiceMuteBars: engine.practiceMode.muteBars,
                bouncingBall: engine.bouncingBall
            };
            window.flPreferenceStorage.setItem(STORAGE_KEY, JSON.stringify(s));
        } catch (e) { /* storage unavailable */ }
    }

    function loadSettings() {
        try {
            const raw = window.flPreferenceStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const s = JSON.parse(raw);

            if (s.bpm) engine.bpm = Math.max(30, Math.min(300, s.bpm));
            if (s.beatsPerBar) engine.beatsPerBar = Math.max(1, Math.min(12, s.beatsPerBar));
            if (Array.isArray(s.beatSubdivisions)) engine.beatSubdivisions = s.beatSubdivisions;
            if (s.soundBank) engine.soundBank = s.soundBank;
            if (s.swingAmount !== undefined) engine.swingAmount = s.swingAmount;
            if (s.humanizeFactor !== undefined) engine.humanizeFactor = s.humanizeFactor;
            if (s.accentStrength !== undefined) engine.accentStrength = s.accentStrength;
            if (s.jazzMode !== undefined) engine.jazzMode = s.jazzMode;
            if (s.screenFlash !== undefined) engine.screenFlash = s.screenFlash;
            if (s.useHaptics !== undefined) engine.useHaptics = s.useHaptics;
            if (s.claveEnabled !== undefined) engine.claveEnabled = s.claveEnabled;
            if (s.clavePattern) engine.clavePattern = s.clavePattern;
            if (s.claveVolume !== undefined) engine.claveVolume = s.claveVolume;
            if (s.accentMode) engine.accentMode = s.accentMode;
            if (s.countInEnabled !== undefined) engine.countIn.enabled = s.countInEnabled;
            if (s.practiceModeEnabled !== undefined) engine.practiceMode.enabled = s.practiceModeEnabled;
            if (s.practicePlayBars !== undefined) engine.practiceMode.playBars = s.practicePlayBars;
            if (s.practiceMuteBars !== undefined) engine.practiceMode.muteBars = s.practiceMuteBars;
            if (s.bouncingBall !== undefined) engine.bouncingBall = s.bouncingBall;

            // Restore slider/control values
            if (ui.bpmSlider) ui.bpmSlider.value = engine.bpm;
            if (ui.soundBank) ui.soundBank.value = engine.soundBank;
            if (ui.swingSlider) {
                ui.swingSlider.value = engine.swingAmount;
                const sv = engine.swingAmount;
                let label = `${sv}%`;
                if (sv <= 51) label += ' (straight)';
                else if (sv >= 65 && sv <= 69) label += ' (triplet)';
                else if (sv > 69) label += ' (heavy)';
                else label += ' (light swing)';
                if (ui.swingValue) ui.swingValue.textContent = label;
            }
            if (ui.humanize) {
                ui.humanize.value = engine.humanizeFactor;
                let htxt = 'None';
                if (engine.humanizeFactor > 0.1) htxt = 'Light';
                if (engine.humanizeFactor > 0.4) htxt = 'Natural';
                if (engine.humanizeFactor > 0.7) htxt = 'Loose';
                if (ui.humanizeVal) ui.humanizeVal.textContent = htxt;
            }
            if (ui.accentStrengthSlider) {
                ui.accentStrengthSlider.value = engine.accentStrength;
                if (ui.accentStrengthValue) ui.accentStrengthValue.textContent = Math.round(engine.accentStrength * 100) + '%';
            }
            if (ui.jazzMode) ui.jazzMode.checked = engine.jazzMode;
            if (ui.screenFlashToggle) ui.screenFlashToggle.checked = engine.screenFlash;
            if (ui.haptics) ui.haptics.checked = engine.useHaptics;
            if (ui.claveToggle) ui.claveToggle.checked = engine.claveEnabled;
            if (ui.claveSelect) ui.claveSelect.value = engine.clavePattern;
            if (ui.claveVolume) ui.claveVolume.value = engine.claveVolume;
            if (ui.accentSelect) ui.accentSelect.value = engine.accentMode;
            if (ui.countInToggle) {
                ui.countInToggle.checked = engine.countIn.enabled;
                if (ui.countInBadge) ui.countInBadge.style.display = engine.countIn.enabled ? 'block' : 'none';
            }
            if (ui.practiceModeToggle) {
                ui.practiceModeToggle.checked = engine.practiceMode.enabled;
                const pmControls = document.getElementById('practiceModeControls');
                if (pmControls) pmControls.style.display = engine.practiceMode.enabled ? 'block' : 'none';
            }
            if (ui.playBarsSlider) {
                ui.playBarsSlider.value = engine.practiceMode.playBars;
                const lbl = document.getElementById('playBarsLabel');
                if (lbl) lbl.textContent = engine.practiceMode.playBars;
            }
            if (ui.muteBarsSlider) {
                ui.muteBarsSlider.value = engine.practiceMode.muteBars;
                const lbl = document.getElementById('muteBarsLabel');
                if (lbl) lbl.textContent = engine.practiceMode.muteBars;
            }
            if (ui.bouncingBallToggle) {
                ui.bouncingBallToggle.checked = engine.bouncingBall;
                if (engine.bouncingBall && ui.bouncingBallCanvas) ui.bouncingBallCanvas.style.display = 'block';
            }
            // Master volume is restored after audio context is created (done in masterVolSlider handler via stored value)
            if (s.masterVolume !== undefined) {
                const masterSlider = document.getElementById('masterVolume');
                if (masterSlider) {
                    masterSlider.value = s.masterVolume;
                    if (masterVolLabel) masterVolLabel.textContent = Math.round(s.masterVolume * 100) + '%';
                }
            }
        } catch (e) { /* malformed storage */ }
    }

    // Hook saveSettings onto relevant changes
    const _origSetBpm = engine.setBpm.bind(engine);
    engine.setBpm = function(val) {
        _origSetBpm(val);
        saveSettings();
    };

    // We also save on key setting changes via a debounced wrapper
    let _saveTimer;
    function deferSave() {
        clearTimeout(_saveTimer);
        _saveTimer = setTimeout(saveSettings, 400);
    }

    // Attach deferSave to the main control events (after initial listeners have been set up)
    [ui.soundBank, ui.swingSlider, ui.humanize, ui.accentStrengthSlider,
     ui.jazzMode, ui.screenFlashToggle, ui.haptics, ui.claveToggle, ui.claveSelect,
     ui.claveVolume, ui.accentSelect, ui.countInToggle, ui.practiceModeToggle,
     ui.playBarsSlider, ui.muteBarsSlider, ui.bouncingBallToggle,
     document.getElementById('masterVolume')
    ].forEach(el => {
        if (el) el.addEventListener('change', deferSave);
        if (el && el.type === 'range') el.addEventListener('input', deferSave);
    });

    // Save when time sig changes — patch updateTimeSig once here
    // (the polyrhythm patch above already replaced it; wrap that version)
    const _origUpdateTimeSigSave = updateTimeSig;
    updateTimeSig = function() {
        _origUpdateTimeSigSave();
        deferSave();
    };

    // ---- INIT ----
    loadSettings();
    // Product pages deep-link here with a song tempo. A valid URL value wins
    // over saved local state so the practice handoff always opens on the
    // requested track tempo; invalid or out-of-range values are ignored.
    try {
        const requestedBpm = Number(new URLSearchParams(window.location.search).get('bpm'));
        if (Number.isInteger(requestedBpm) && requestedBpm >= 30 && requestedBpm <= 300) {
            engine.setBpm(requestedBpm);
        }
    } catch (e) { }
    engine.syncBeatSubdivisions();
    updateJazzIcon();
    updateBeatDots();
    updateBpmUI();
    buildSubdivSelector();
    buildAccentGrid();
    updateClaveVisual();
    buildSubdivGrid();
    updateStats();

    // ---- iOS AUDIO CONTEXT UNLOCK ----
    // AudioContext is created lazily; on iOS we show a tap-to-enable prompt
    // only when the context already exists and is suspended (e.g. after background).
    const iosUnlock = document.getElementById('iosAudioUnlock');
    const iosUnlockBtn = document.getElementById('iosUnlockBtn');

    function checkIosUnlock() {
        if (engine.ctx && engine.ctx.state === 'suspended') {
            if (iosUnlock) iosUnlock.style.display = 'flex';
        }
    }
    // Re-check after first interaction creates the context
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkIosUnlock();
    });

    if (iosUnlockBtn) {
        iosUnlockBtn.addEventListener('click', () => {
            engine.resumeContext();
            if (iosUnlock) iosUnlock.style.display = 'none';
        });
    }

    // Stop audio when page is hidden (rule 4)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && engine.isPlaying) {
            engine.stop();
            // Update UI
            ui.playBtn?.classList.remove('active');
            const iconPlay = ui.playBtn?.querySelector('.icon-play');
            const iconStop = ui.playBtn?.querySelector('.icon-stop');
            if (iconPlay) iconPlay.style.display = 'block';
            if (iconStop) iconStop.style.display = 'none';
        }
    });
    });
});
